import { FormEvent, useEffect, useMemo, useState } from 'react';
import { adminApi, ApiError } from '../lib/api';
import type { AIDraftProposal, AIDraftResult, TemplateKey } from '../../shared/types';
import { buildManualPrompt, parseManualResponse } from '../../shared/aiDraftPrompt';
import { buildAIDraftDoc } from '../../shared/buildAIDraftDoc';
import {
  DASHBOARD_SLUG_HINT,
  DASHBOARD_SLUG_HTML_PATTERN,
  normaliseDashboardSlugInput,
} from '../../shared/dashboardSlug';
import Eyebrow from './Eyebrow';
import Button from './Button';

const TEMPLATE_LABELS: Record<TemplateKey, string> = {
  brand_film: 'Brand film',
  testimonial: 'Testimonial',
  explainer: 'Explainer',
  social_short: 'Social / short-form',
  fb_ad_campaign: 'FB Ad Campaign',
  interview_brief: 'Interview brief',
};

const TEMPLATES: TemplateKey[] = [
  'brand_film',
  'testimonial',
  'explainer',
  'social_short',
  'fb_ad_campaign',
];

interface Props {
  onClose: () => void;
  onCreated: (firstId: string) => void;
}

interface EditableProposal extends AIDraftProposal {
  enabled: boolean;
}

type Step = 'paste' | 'manual-export' | 'review';

export default function TranscriptDraftModal({ onClose, onCreated }: Props) {
  const [step, setStep] = useState<Step>('paste');
  const [transcript, setTranscript] = useState('');
  const [clientName, setClientName] = useState('');
  const [dashboardSlug, setDashboardSlug] = useState('');
  const [proposals, setProposals] = useState<EditableProposal[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  // -- Path A: API call (uses ANTHROPIC_API_KEY on the worker) --
  async function handleGenerateApi() {
    if (!transcript.trim()) {
      setError('Paste a transcript first.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await adminApi.draftFromTranscript(transcript);
      setClientName(res.client_name);
      setProposals(res.proposals.map((p) => ({ ...p, enabled: true })));
      setStep('review');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Drafting failed.');
      }
    } finally {
      setBusy(false);
    }
  }

  // -- Path B: manual via Claude.ai chat (uses subscription, no API key) --
  function handleStartManual() {
    if (!transcript.trim()) {
      setError('Paste a transcript first.');
      return;
    }
    setError(null);
    setStep('manual-export');
  }

  function handleManualParsed(result: AIDraftResult) {
    // Build doc_json client-side using the shared builder so the Review step
    // gets the same proposal shape the API path produces.
    const built: EditableProposal[] = result.videos.map((video) => ({
      title: video.title,
      template_key: video.template_key,
      overview: video.overview,
      objective: video.objective,
      doc_json: JSON.stringify(buildAIDraftDoc(video, result.client_name)),
      enabled: true,
    }));
    setClientName(result.client_name);
    setProposals(built);
    setError(null);
    setStep('review');
  }

  async function handleCreate() {
    const enabled = proposals.filter((p) => p.enabled);
    if (enabled.length === 0) {
      setError('Select at least one video to create.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const slug = dashboardSlug.trim();
      const results = await Promise.all(
        enabled.map((p) =>
          adminApi.createScript({
            title: p.title,
            client_name: clientName,
            template_key: p.template_key,
            doc_json: p.doc_json,
            ...(slug ? { dashboard_slug: slug } : {}),
          }),
        ),
      );
      const firstId = results[0]?.id;
      if (firstId) onCreated(firstId);
      else onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Creation failed.');
      setBusy(false);
    }
  }

  function updateProposal(index: number, patch: Partial<EditableProposal>) {
    setProposals((prev) =>
      prev.map((p, i) => (i === index ? { ...p, ...patch } : p)),
    );
  }

  return (
    <div
      className="fixed inset-0 bg-ink-900/70 backdrop-blur-md flex items-stretch md:items-center justify-center px-0 md:px-6 py-0 md:py-8 z-50 animate-fade-in"
      onClick={busy ? undefined : onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="
          w-full md:max-w-3xl md:max-h-[90vh] overflow-hidden
          bg-card border border-line md:rounded-md
          flex flex-col animate-scale-in
        "
      >
        {step === 'paste' && (
          <PasteStep
            transcript={transcript}
            onChange={setTranscript}
            busy={busy}
            error={error}
            onGenerateApi={handleGenerateApi}
            onStartManual={handleStartManual}
            onCancel={onClose}
          />
        )}
        {step === 'manual-export' && (
          <ManualExportStep
            transcript={transcript}
            error={error}
            onError={setError}
            onParsed={handleManualParsed}
            onBack={() => setStep('paste')}
          />
        )}
        {step === 'review' && (
          <ReviewStep
            clientName={clientName}
            onClientNameChange={setClientName}
            dashboardSlug={dashboardSlug}
            onDashboardSlugChange={setDashboardSlug}
            proposals={proposals}
            updateProposal={updateProposal}
            busy={busy}
            error={error}
            onBack={() => setStep('paste')}
            onCreate={handleCreate}
          />
        )}
      </div>
    </div>
  );
}

// ---------- Paste step ----------

interface PasteStepProps {
  transcript: string;
  onChange: (v: string) => void;
  busy: boolean;
  error: string | null;
  onGenerateApi: () => void;
  onStartManual: () => void;
  onCancel: () => void;
}

function PasteStep({
  transcript,
  onChange,
  busy,
  error,
  onGenerateApi,
  onStartManual,
  onCancel,
}: PasteStepProps) {
  return (
    <div className="flex flex-col flex-1 min-h-0">
      <header className="px-6 md:px-8 py-5 border-b border-line shrink-0">
        <Eyebrow accent>// Draft from transcript</Eyebrow>
        <h2 className="font-display font-bold text-[24px] leading-[1.15] tracking-[-0.01em] mt-3 mb-2">
          Paste your kick-off call transcript
        </h2>
        <p className="text-muted text-[14px] leading-[1.55] max-w-prose">
          Claude reads the transcript, identifies each video discussed, picks a template per video, and produces first-draft scripts you can review before creating.
        </p>
      </header>

      <div className="flex-1 overflow-y-auto px-6 md:px-8 py-5">
        <textarea
          value={transcript}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste the transcript here. Anything from a few hundred words to a full one-hour call works."
          autoFocus
          disabled={busy}
          rows={14}
          className="
            w-full px-4 py-3 bg-elevated border border-line rounded-sm
            text-fg text-[14px] leading-[1.55] resize-y
            placeholder:text-faint
            focus:border-accent focus:shadow-focus focus:outline-none
            transition-all duration-150
            disabled:opacity-50
          "
        />
        {error && <p className="text-[13px] text-accent mt-3">{error}</p>}
      </div>

      <footer className="flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-3 px-6 md:px-8 py-4 border-t border-line bg-elevated shrink-0">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <div className="flex flex-col md:flex-row gap-2 md:items-center">
          <Button
            type="button"
            variant="secondary"
            onClick={onStartManual}
            disabled={busy || !transcript.trim()}
            title="Use your Claude.ai subscription instead of the API"
          >
            Generate via Claude.ai
          </Button>
          <Button
            type="button"
            variant="primary"
            glow
            onClick={onGenerateApi}
            disabled={busy || !transcript.trim()}
            title="Use the Anthropic API (requires ANTHROPIC_API_KEY)"
          >
            {busy ? 'Drafting…' : 'Generate via API'}
          </Button>
        </div>
      </footer>
    </div>
  );
}

// ---------- Manual export step ----------

interface ManualExportStepProps {
  transcript: string;
  error: string | null;
  onError: (msg: string | null) => void;
  onParsed: (result: AIDraftResult) => void;
  onBack: () => void;
}

function ManualExportStep({
  transcript,
  error,
  onError,
  onParsed,
  onBack,
}: ManualExportStepProps) {
  const prompt = useMemo(() => buildManualPrompt(transcript), [transcript]);
  const [copied, setCopied] = useState(false);
  const [response, setResponse] = useState('');

  async function handleCopyAndOpen() {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      window.open('https://claude.ai/new', '_blank', 'noopener,noreferrer');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      onError('Could not copy to clipboard. Select the prompt manually and copy.');
    }
  }

  function handleParse(e: FormEvent) {
    e.preventDefault();
    const parsed = parseManualResponse(response);
    if (!parsed) {
      onError(
        "Couldn't read Claude's response as JSON. Make sure you copied the entire JSON code block from Claude's reply.",
      );
      return;
    }
    onParsed(parsed);
  }

  return (
    <form onSubmit={handleParse} className="flex flex-col flex-1 min-h-0">
      <header className="px-6 md:px-8 py-5 border-b border-line shrink-0">
        <Eyebrow accent>// Generate via Claude.ai · 2 of 3</Eyebrow>
        <h2 className="font-display font-bold text-[22px] leading-[1.15] tracking-[-0.01em] mt-3 mb-2">
          Send to Claude.ai, then paste the reply back
        </h2>
        <p className="text-muted text-[14px] leading-[1.55] max-w-prose">
          Step 1 — copy the prompt below and paste it into a new Claude.ai chat. Step 2 — copy Claude's
          JSON reply and paste it into the box at the bottom of this page.
        </p>
      </header>

      <div className="flex-1 overflow-y-auto px-6 md:px-8 py-5 space-y-6">
        {/* Prompt to copy */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
              Step 1 · Copy this prompt
            </span>
            <Button
              type="button"
              variant="primary"
              size="sm"
              glow
              onClick={handleCopyAndOpen}
            >
              {copied ? '✓ Copied · Claude.ai opened' : 'Copy + open Claude.ai'}
            </Button>
          </div>
          <textarea
            readOnly
            value={prompt}
            rows={10}
            onFocus={(e) => e.currentTarget.select()}
            className="
              w-full px-4 py-3 bg-elevated border border-line rounded-sm
              text-fg text-[12px] leading-[1.55] font-mono resize-y
              focus:border-accent focus:shadow-focus focus:outline-none
              transition-all duration-150
            "
          />
        </section>

        {/* Paste response */}
        <section>
          <div className="mb-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
              Step 2 · Paste Claude's reply
            </span>
            <p className="text-faint text-[12px] mt-1 leading-[1.55]">
              Copy the entire response from Claude (including the <code className="font-mono">```json</code>{' '}
              fence if present) and paste it here.
            </p>
          </div>
          <textarea
            value={response}
            onChange={(e) => setResponse(e.target.value)}
            placeholder="Paste Claude's JSON response here…"
            rows={10}
            className="
              w-full px-4 py-3 bg-elevated border border-line rounded-sm
              text-fg text-[12px] leading-[1.55] font-mono resize-y
              placeholder:text-faint
              focus:border-accent focus:shadow-focus focus:outline-none
              transition-all duration-150
            "
          />
        </section>

        {error && <p className="text-[13px] text-accent">{error}</p>}
      </div>

      <footer className="flex items-center justify-between gap-3 px-6 md:px-8 py-4 border-t border-line bg-elevated shrink-0">
        <Button type="button" variant="ghost" onClick={onBack}>
          ← Back
        </Button>
        <Button type="submit" variant="primary" glow disabled={!response.trim()}>
          Read response →
        </Button>
      </footer>
    </form>
  );
}

// ---------- Review step ----------

interface ReviewStepProps {
  clientName: string;
  onClientNameChange: (v: string) => void;
  dashboardSlug: string;
  onDashboardSlugChange: (v: string) => void;
  proposals: EditableProposal[];
  updateProposal: (i: number, patch: Partial<EditableProposal>) => void;
  busy: boolean;
  error: string | null;
  onBack: () => void;
  onCreate: () => void;
}

function ReviewStep({
  clientName,
  onClientNameChange,
  dashboardSlug,
  onDashboardSlugChange,
  proposals,
  updateProposal,
  busy,
  error,
  onBack,
  onCreate,
}: ReviewStepProps) {
  const enabledCount = proposals.filter((p) => p.enabled).length;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <header className="px-6 md:px-8 py-5 border-b border-line shrink-0">
        <Eyebrow accent>// Review drafts</Eyebrow>
        <h2 className="font-display font-bold text-[24px] leading-[1.15] tracking-[-0.01em] mt-3 mb-2">
          {proposals.length} draft{proposals.length === 1 ? '' : 's'} ready
        </h2>
        <p className="text-muted text-[14px] leading-[1.55] max-w-prose">
          Tweak titles or templates if Claude got something wrong. Untick any draft you don't want
          to create. You can refine each script after they're created.
        </p>
      </header>

      <div className="flex-1 overflow-y-auto px-6 md:px-8 py-5 space-y-4">
        <label className="block">
          <span className="block font-mono text-[11px] uppercase tracking-[0.08em] text-muted mb-2">
            Client name
          </span>
          <input
            value={clientName}
            onChange={(e) => onClientNameChange(e.target.value)}
            disabled={busy}
            className="
              w-full px-4 py-2.5 bg-elevated border border-line rounded-sm
              text-fg text-[14px]
              focus:border-accent focus:shadow-focus focus:outline-none
              transition-all duration-150
            "
          />
        </label>

        <label className="block">
          <span className="block font-mono text-[11px] uppercase tracking-[0.08em] text-muted mb-2">
            Dashboard slug (optional)
          </span>
          <input
            value={dashboardSlug}
            onChange={(e) => onDashboardSlugChange(normaliseDashboardSlugInput(e.target.value))}
            disabled={busy}
            placeholder="e.g. gigi-instyle"
            pattern={DASHBOARD_SLUG_HTML_PATTERN}
            title={DASHBOARD_SLUG_HINT}
            className="
              w-full px-4 py-2.5 bg-elevated border border-line rounded-sm
              text-fg text-[14px]
              focus:border-accent focus:shadow-focus focus:outline-none
              transition-all duration-150
            "
          />
        </label>

        <ul className="space-y-2 mt-2">
          {proposals.map((p, i) => (
            <li
              key={i}
              className={`
                bg-elevated border rounded-sm p-4 transition-colors duration-200
                ${p.enabled ? 'border-line' : 'border-line/40 opacity-50'}
              `}
            >
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={p.enabled}
                  onChange={(e) => updateProposal(i, { enabled: e.target.checked })}
                  disabled={busy}
                  aria-label={`Include "${p.title}"`}
                  className="mt-1.5 w-4 h-4 accent-[var(--color-accent)]"
                />
                <div className="flex-1 min-w-0 space-y-2">
                  <input
                    value={p.title}
                    onChange={(e) => updateProposal(i, { title: e.target.value })}
                    disabled={busy || !p.enabled}
                    placeholder="Title"
                    className="
                      w-full px-3 py-2 bg-card border border-line rounded-sm
                      text-fg font-display font-semibold text-[16px]
                      focus:border-accent focus:shadow-focus focus:outline-none
                      transition-all duration-150
                      disabled:opacity-50
                    "
                  />
                  <select
                    value={p.template_key}
                    onChange={(e) =>
                      updateProposal(i, { template_key: e.target.value as TemplateKey })
                    }
                    disabled={busy || !p.enabled}
                    className="
                      w-full px-3 py-2 bg-card border border-line rounded-sm
                      text-fg text-[13px]
                      focus:border-accent focus:shadow-focus focus:outline-none
                      transition-all duration-150
                      disabled:opacity-50
                    "
                  >
                    {TEMPLATES.map((t) => (
                      <option key={t} value={t}>
                        {TEMPLATE_LABELS[t]}
                      </option>
                    ))}
                  </select>
                  {p.overview && (
                    <p className="text-muted text-[12px] leading-snug">
                      <span className="text-faint">Overview · </span>
                      {p.overview}
                    </p>
                  )}
                  {p.objective && (
                    <p className="text-muted text-[12px] leading-snug">
                      <span className="text-faint">Objective · </span>
                      {p.objective}
                    </p>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>

        {error && <p className="text-[13px] text-accent mt-3">{error}</p>}
      </div>

      <footer className="flex items-center justify-between gap-3 px-6 md:px-8 py-4 border-t border-line bg-elevated shrink-0">
        <Button type="button" variant="ghost" onClick={onBack} disabled={busy}>
          ← Back
        </Button>
        <div className="flex gap-2">
          <span className="hidden md:inline-flex items-center font-mono text-[11px] uppercase tracking-[0.12em] text-faint mr-2">
            {enabledCount} of {proposals.length} selected
          </span>
          <Button
            type="button"
            variant="primary"
            glow
            onClick={onCreate}
            disabled={busy || enabledCount === 0 || !clientName.trim()}
          >
            {busy
              ? 'Creating…'
              : `Create ${enabledCount} script${enabledCount === 1 ? '' : 's'}`}
          </Button>
        </div>
      </footer>
    </div>
  );
}
